// --- Dependencies -------------------------------------------------------------------------------
import { useRef } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyTeamMember } from "@/types";
import { pressToOpen, rightClickToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Badge from "@/UI/atoms/Badge";
import Icon from "@/UI/atoms/Icon";
import "../selection.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    member: TyTeamMember;
    isMe: boolean;
    canManage: boolean;
    onActions: (anchor: DOMRect) => void;
    /** Picked by the bulk bar. Draws the ring; the screen decides what pressing the card then means. */
    isSelected?: boolean;
    /**
     * Only while the bulk bar is up. This is the one card in the app with no main action of its own —
     * a person is not a record you open — so pressing it means something only during a selection.
     */
    onPick?: () => void;
    /**
     * The bar is up. Every other control on the card is **gone, not disabled** — a half-live card
     * looks pressable and does something nobody asked for — and the menu button's place is taken by
     * the tick, so nothing moves and nothing overlaps.
     */
    isPicking?: boolean;
};

/** One person. Presentational — the screen owns the data and the actions. */
export default function MemberCard({ member, isMe, canManage, onActions, isSelected, onPick, isPicking }: Props) {
    const t = useTranslation();
    const buttonRef = useRef<HTMLButtonElement>(null);

    return (
        <article
            className={`member-card pick${isSelected ? " pick--on" : ""}${isPicking ? " pick--picking" : ""}`}
            role={onPick ? "button" : undefined}
            tabIndex={onPick ? 0 : undefined}
            aria-pressed={onPick ? isSelected : undefined}
            onClick={onPick}
            onKeyDown={onPick ? pressToOpen(onPick) : undefined}
            onContextMenu={isPicking || !canManage ? undefined : rightClickToOpen(onActions)}
        >
            <div className="member-card__row">
                <Avatar name={member.name} picture={member.picture} size={44} bordered />

                <div className="member-card__identity">
                    <span className="member-card__name">
                        {member.name}
                        {isMe && <span className="member-card__me">{t.team.you}</span>}
                    </span>
                    <bdi className="member-card__phone" dir="ltr">
                        {member.phone}
                    </bdi>
                </div>

                {/* The tick takes the menu's place while the bar is up — and a person this role
                    cannot act on gets neither, so they are never picked into a write that skips them. */}
                {isPicking
                    ? onPick && (
                          <span className={`pick__box${isSelected ? " pick__box--on" : ""}`} aria-hidden="true">
                              <Icon name="check" size={18} />
                          </span>
                      )
                    : canManage && (
                          <button
                              ref={buttonRef}
                              type="button"
                              className="member-card__actions"
                              onClick={(event) => {
                                  event.stopPropagation();
                                  const rect = buttonRef.current?.getBoundingClientRect();
                                  if (rect) onActions(rect);
                              }}
                              aria-label={t.common.more}
                          >
                              <Icon name="more" size={20} />
                          </button>
                      )}
            </div>

            <div className="member-card__tags">
                <Badge label={t.roles[member.role]} tone={member.role === "owner" ? "gold" : "quiet"} />
                {member.isPasswordTemporary && <Badge label={t.team.temporaryPassword} />}
            </div>
        </article>
    );
}
